<template>
    <div
        class="dropzone-area"
        :class="{ 'dragover': isDragging, 'uploading': uploading }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="triggerFileInput"
    >
        <input
            type="file"
            ref="fileInput"
            @change="handleFileSelect"
            accept=".csv,.xlsx,.xls"
            class="d-none"
        />

        <div v-if="uploading" class="upload-progress">
            <div class="progress mb-2" style="height: 20px;">
                <div
                    class="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    :style="{ width: uploadProgress + '%' }"
                >
                    {{ uploadProgress }}%
                </div>
            </div>
            <p class="text-muted mb-0">{{ trans('Uploading...') }}</p>
        </div>

        <div v-else class="dropzone-content">
            <i class="icofont-cloud-upload icofont-3x text-muted"></i>
            <p class="mt-2 mb-1">{{ trans('Drag and drop a file here') }}</p>
            <p class="text-muted small mb-0">{{ trans('or click to select a file') }}</p>
            <p class="text-muted small mb-0">{{ trans('Supported formats: CSV, Excel (.xlsx, .xls)') }}</p>
            <p class="text-muted small">{{ trans('Maximum file size:') }} 10MB</p>
        </div>

        <div v-if="error" class="alert alert-danger mt-3 mb-0">
            {{ error }}
        </div>
    </div>
</template>

<script>
export default {
    props: {
        uploadUrl: {
            type: String,
            required: true,
        },
        maxFileSize: {
            type: Number,
            default: 10 * 1024 * 1024, // 10MB
        },
    },

    data() {
        return {
            isDragging: false,
            uploading: false,
            uploadProgress: 0,
            error: null,
        };
    },

    methods: {
        triggerFileInput() {
            if (!this.uploading) {
                this.$refs.fileInput.click();
            }
        },

        handleDrop(event) {
            this.isDragging = false;
            const files = event.dataTransfer.files;
            if (files.length > 0) {
                this.uploadFile(files[0]);
            }
        },

        handleFileSelect(event) {
            const files = event.target.files;
            if (files.length > 0) {
                this.uploadFile(files[0]);
            }
            // Reset input so same file can be selected again
            event.target.value = '';
        },

        async uploadFile(file) {
            this.error = null;

            if (!this.isValidFile(file)) {
                // isValidFile already sets specific error for size issues
                if (!this.error) {
                    this.error = this.trans('Please select a valid CSV or Excel file.');
                }
                return;
            }

            this.uploading = true;
            this.uploadProgress = 0;

            const formData = new FormData();
            formData.append('file', file);

            try {
                const response = await axios.post(this.uploadUrl, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    onUploadProgress: (progressEvent) => {
                        this.uploadProgress = Math.round(
                            (progressEvent.loaded / progressEvent.total) * 100
                        );
                    },
                });

                this.$emit('upload-success', response.data);
            } catch (error) {
                const message = error.response?.data?.error ||
                    error.response?.data?.message ||
                    this.trans('Failed to upload file');
                this.error = message;
                this.$emit('upload-error', message);
            } finally {
                this.uploading = false;
                this.uploadProgress = 0;
            }
        },

        isValidFile(file) {
            // Check file size first
            if (file.size > this.maxFileSize) {
                const maxSizeMB = Math.round(this.maxFileSize / 1024 / 1024);
                this.error = this.trans('File is too large. Maximum size is :size MB.', { size: maxSizeMB });
                return false;
            }

            const validTypes = [
                'text/csv',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ];
            const validExtensions = ['.csv', '.xlsx', '.xls'];

            const extension = '.' + file.name.split('.').pop().toLowerCase();

            return validTypes.includes(file.type) || validExtensions.includes(extension);
        },
    },
};
</script>

<style scoped>
.dropzone-area {
    border: 2px dashed #dee2e6;
    border-radius: 0.5rem;
    padding: 2rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    min-height: 200px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.dropzone-area:hover {
    border-color: #007bff;
    background-color: #f8f9fa;
}

.dropzone-area.dragover {
    border-color: #007bff;
    background-color: #e3f2fd;
}

.dropzone-area.uploading {
    cursor: default;
}

.dropzone-content {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.upload-progress {
    width: 100%;
    max-width: 300px;
}
</style>
